// src/components/PartsReturnEntry.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc, collection, doc, getDoc, updateDoc, serverTimestamp, onSnapshot,
  runTransaction, // ← SAME as ReturnsEntry.jsx
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

/* ----------------------- Config ----------------------- */

const SECTIONS = [
  { id: "return-part", label: "Return & Part" },
  { id: "codes",       label: "Reported Reason & Fault Found" },
  { id: "procedure",   label: "Test Procedure" },
  { id: "notes",       label: "Notes" },
  { id: "photos",      label: "Photos" },
];

const STATUS_OPTIONS = [
  { value: "new",                 label: "New" },
  { value: "waiting_collection",  label: "Waiting for collection" },
  { value: "waiting_processing",  label: "Waiting to be processed" },
  { value: "in_progress",         label: "In progress" },
  { value: "straggler",           label: "Straggler" },
];

const ALLOW_COMPLETE_HERE = true;

const INITIAL_FORM = {
  returnsNoteNumber: "", // ← will be allocated & locked (same as ReturnsEntry.jsx)
  serialNumber: "",
  sretNumber: "",
  partCode: "",
  partTitle: "",
  partDescription: "",
  reportedCode: "",
  faultFoundCode: "",
  internalNotes: "",
};

const INITIAL_TEST = {}; // dynamic per-template

/* ----------------------- Small utils ----------------------- */

const useDebouncedEffect = (fn, deps, delay) => {
  useEffect(() => {
    const t = setTimeout(fn, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

const labelize = (k) =>
  k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

/* ----------------------- Returns number helpers (EXACT COPY STYLE) ----------------------- */

// Same doc as in ReturnsEntry.jsx
const COUNTERS_REF = doc(db, "appData", "counters");

// RN text like RN-000001
function formatReturnsNoteNumber(n) {
  return `RN-${String(n).padStart(6, "0")}`;
}

// Atomic increment; creates doc if missing (same as ReturnsEntry.jsx)
async function getNextReturnsNumber() {
  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(COUNTERS_REF);
    if (!snap.exists()) {
      tx.set(COUNTERS_REF, { despatchCounter: 0, returnsCounter: 1 });
      return 1;
    }
    const cur = Number(snap.data().returnsCounter || 0);
    const n = cur + 1;
    tx.update(COUNTERS_REF, { returnsCounter: n });
    return n;
  });
  return next;
}

/* ----------------------- Component ----------------------- */

export default function PartsReturnEntry() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const urlDraftId = new URLSearchParams(search).get("draft"); // /returns/parts/new?draft=<id>

  const [form, setForm] = useState(INITIAL_FORM);
  const [test, setTest] = useState(INITIAL_TEST);

  // Workflow status
  const [status, setStatus] = useState("new");
  const [statusUpdatedAt, setStatusUpdatedAt] = useState(null);

  // Template for the selected part
  const [template, setTemplate] = useState(null); // {partCode,title,description,codes,steps}

  // UX
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [errors, setErrors] = useState({});

  // Draft
  const [draftId, setDraftId] = useState(urlDraftId || null);

  // Dirty tracking
  const snapshotKey = JSON.stringify({ form, test, templateCode: template?.partCode || "", status });
  const [lastSavedKey, setLastSavedKey] = useState(snapshotKey);
  const dirty = snapshotKey !== lastSavedKey;

  // side-nav highlight
  const observer = useRef(null);
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -70% 0px", threshold: 0 };
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id));
    }, opts);
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.current.observe(el);
    });
    return () => observer.current?.disconnect();
  }, []);

  /* --------- Load draft if present --------- */

  useEffect(() => {
    (async () => {
      if (!urlDraftId) return;
      const snap = await getDoc(doc(db, "returns_parts_draft", urlDraftId));
      if (!snap.exists()) {
        navigate("/returns/parts/new", { replace: true });
        return;
      }
      const d = snap.data();
      d.form && setForm(d.form);
      d.test && setTest(d.test);
      setDraftId(urlDraftId);
      setStatus(d.status || "new");
      setStatusUpdatedAt(d.statusUpdatedAt || null);
      setLastSavedKey(JSON.stringify({
        form: d.form || {},
        test: d.test || {},
        templateCode: d.template?.partCode || "",
        status: d.status || "new",
      }));

      if (d.template?.partCode) {
        setTemplate(d.template);
      } else if (d.form?.partCode) {
        await loadTemplate(d.form.partCode);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlDraftId]);

  /* --------- Load part template by code (ONE read) --------- */

  const loadTemplate = async (partCode) => {
    const code = (partCode || "").trim();
    if (!code) {
      setTemplate(null);
      return;
    }
    const ref = doc(db, "part_templates", code);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      setTemplate(null);
      setForm(prev => ({
        ...prev,
        partCode: code,
        partTitle: "",
        partDescription: "",
        reportedCode: "",
        faultFoundCode: "",
      }));
      setTest({});
      return;
    }
    const tpl = snap.data();
    setTemplate(tpl);
    // hydrate form display fields
    setForm(prev => ({
      ...prev,
      partCode: tpl.partCode,
      partTitle: tpl.title || "",
      partDescription: tpl.description || "",
      // clear selections when a new template loads
      reportedCode: "",
      faultFoundCode: "",
    }));
    // initialize test state from steps
    setTest(prev => {
      const next = { ...prev };
      (tpl.steps || []).forEach(step => {
        if (!(step.id in next)) {
          next[step.id] = step.type === "check" ? false : "";
        }
      });
      // drop stale keys no longer in steps
      Object.keys(next).forEach(k => {
        if (!(tpl.steps || []).some(s => s.id === k)) delete next[k];
      });
      return next;
    });
  };

  /* --------- Validation & progress --------- */

  const validate = useMemo(() => {
    return (f, t) => {
      const e = {};
      // RN is auto-generated (no typing), but ensure present before submit
      if (!f.returnsNoteNumber?.trim()) e.returnsNoteNumber = "Returns note number is required.";

      if (!f.serialNumber?.trim())      e.serialNumber      = "Serial number is required.";
      if (!f.partCode?.trim())          e.partCode          = "Enter a part code and load the template.";
      if (!template?.partCode)          e.template          = "No template loaded for that part code.";

      if (!f.reportedCode)    e.reportedCode    = "Pick what was reported.";
      if (!f.faultFoundCode)  e.faultFoundCode  = "Pick what you found.";

      (template?.steps || []).forEach(step => {
        if (!step.required) return;
        const val = t[step.id];
        const missing = step.type === "check" ? !val : String(val || "").trim() === "";
        if (missing) e[`step:${step.id}`] = `Required: ${step.label}`;
      });

      return e;
    };
  }, [template]);

  const progress = useMemo(() => {
    let s = 0;
    if (form.returnsNoteNumber) s += 25;
    if (form.serialNumber)      s += 25;
    if (template?.partCode)     s += 10;
    if (form.reportedCode)      s += 20;
    if (form.faultFoundCode)    s += 20;

    if (template?.steps?.length) {
      const total = template.steps.length;
      const done = template.steps.filter(st => {
        const v = test[st.id];
        return st.type === "check" ? !!v : String(v || "").trim().length > 0;
      }).length;
      s += Math.min(20, Math.round((done / total) * 20));
    }
    return Math.min(100, s);
  }, [form, test, template]);

  /* --------- Draft save / autosave --------- */

  const fns = useMemo(() => getFunctions(undefined, "europe-west2"), []);

  const saveDraft = async (manual = false) => {
    try {
      setSaveError("");
      setIsSaving(true);

      // *** EXACT BEHAVIOUR from ReturnsEntry.jsx:
      // On first-ever draft save, allocate and lock an RN once.
      let ensuredForm = form;
      if (!draftId && !form.returnsNoteNumber) {
        const nextNum = await getNextReturnsNumber();
        ensuredForm = { ...form, returnsNoteNumber: formatReturnsNoteNumber(nextNum) };
        setForm(ensuredForm); // keep UI in sync
      }

      const payload = {
        form: ensuredForm,
        test,
        template: template ? {
          partCode: template.partCode,
          title: template.title || "",
          description: template.description || "",
          codes: template.codes || [],
          steps: template.steps || []
        } : null,
        status,
        statusUpdatedAt: statusUpdatedAt || serverTimestamp(),
        progress,
        updatedAt: serverTimestamp(),
      };

      if (!draftId) {
        const ref = await addDoc(collection(db, "returns_parts_draft"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setDraftId(ref.id);

        // Create /ReturnsImages/{draftId} and persist storage on the draft (same as returns)
        try {
          const create = httpsCallable(fns, "spEnsureFolder");
          const { data } = await create({
            type: "returnsImage",
            subId: ref.id,
          });
          await updateDoc(doc(db, "returns_parts_draft", ref.id), {
            storage: {
              provider: "sharepoint",
              folderId: data.folderItemId,
              folderUrl: data.folderWebUrl,
            },
          });
        } catch (e) {
          console.error("Returns (parts) folder create failed:", e); // non-fatal
        }


        const q = new URLSearchParams(search);
        q.set("draft", ref.id);
        navigate({ search: `?${q.toString()}` }, { replace: true });
      } else {
        await updateDoc(doc(db, "returns_parts_draft", draftId), payload);
      }
      setLastSavedKey(JSON.stringify({ form: ensuredForm, test, templateCode: template?.partCode || "", status }));
      if (manual) console.info("Draft saved.");
    } catch (e) {
      console.error(e);
      setSaveError("Could not save draft.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (!dirty || isSubmitting) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, isSubmitting]);

  /* --------- Submit --------- */

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const eMap = validate(form, test);
    setErrors(eMap);
    if (Object.keys(eMap).length) {
      const first = Object.keys(eMap)[0];
      const sectionMap = {
        returnsNoteNumber: "return-part",
        serialNumber: "return-part",
        partCode: "return-part",
        template: "return-part",
        reportedCode: "codes",
        faultFoundCode: "codes",
      };
      const key = first.startsWith("step:") ? "procedure" : (sectionMap[first] || "return-part");
      document.getElementById(key)?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    try {
      setIsSubmitting(true);
      if (dirty) await saveDraft(false); // flush latest (RN should already exist from first save)

// AFTER (parity with cylinders):
      const finalPayload = {
        createdAt: serverTimestamp(),
        source: "pthelpdesk",
        version: 3,               // ← align with cylinders
        status,                   // whatever you chose in the form
        statusUpdatedAt: serverTimestamp(),
        adminNotes: "",           // ← parity fields so Admin box works the same
        notesUpdatedAt: null,     // ← parity fields so timestamps are clean
        draftRef: draftId || null,
        payload: {
          form,
          test,
          templateSnapshot: template ? {
            partCode: template.partCode,
            title: template.title || "",
            description: template.description || "",
            codes: template.codes || [],
            steps: template.steps || []
          } : null
        },
      };
      const ref = await addDoc(collection(db, "returns_parts"), finalPayload);

      if (draftId) {
        await updateDoc(doc(db, "returns_parts_draft", draftId), {
          status: "submitted",
          submittedAt: serverTimestamp(),
          finalRefId: ref.id,
        });
      }

      startNewReport();
    } catch (err) {
      console.error(err);
      setErrors((p) => ({ ...p, submit: "Submit failed. Try again." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  /* --------- Helpers --------- */

  const startNewReport = () => {
    if (dirty && !window.confirm("Discard unsaved changes and start a new report?")) return;
    setForm(INITIAL_FORM);
    setTest(INITIAL_TEST);
    setTemplate(null);
    setErrors({});
    setDraftId(null);
    setStatus("new");
    setStatusUpdatedAt(null);
    setLastSavedKey(JSON.stringify({
      form: INITIAL_FORM,
      test: INITIAL_TEST,
      templateCode: "",
      status: "new",
    }));
    navigate("/returns/parts/new", { replace: true }); // no draft param
  };

  const setF = (patch) => setForm((p) => ({ ...p, ...patch }));

  const codesActive = useMemo(
    () => (template?.codes || []).filter(c => !c.deprecated),
    [template]
  );

  /* ----------------------- Render ----------------------- */

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Returns — Parts</h1>
        {/* Status selector */}
        <div className="ml-4">
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              setStatus(next);
              setStatusUpdatedAt(new Date().toISOString());
            }}
            className="text-sm rounded-md border border-gray-300 px-3 py-2"
            title="Workflow status"
          >
            {STATUS_OPTIONS
              .filter(o => (ALLOW_COMPLETE_HERE ? true : o.value !== "complete"))
              .map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
          </select>
        </div>
        <span className="ml-auto text-sm text-gray-500">
          {isSaving ? "Saving draft…" : dirty ? "Unsaved changes" : "All changes saved"}
        </span>
        {saveError && <span className="text-sm text-red-600">{saveError}</span>}
        <button type="button" onClick={() => saveDraft(true)} className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">
          Save draft
        </button>
        <button type="button" onClick={startNewReport} className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">
          New report
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Side nav */}
        <aside className="lg:sticky lg:top-4 h-max bg-white border border-gray-200 rounded-xl p-3">
          <nav className="space-y-1">
            {SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
                className={`w-full text-left px-3 py-2 rounded-md transition ${activeId === id ? "bg-indigo-600 text-white" : "hover:bg-gray-100 text-gray-800"}`}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="mt-4 p-3 bg-gray-50 border border-dashed rounded-md text-xs text-gray-600">
            <div><b>Draft:</b> {draftId ? draftId.slice(-8) : "none"}</div>
            <div><b>Progress:</b> {Math.round(progress)}%</div>
          </div>
        </aside>

        {/* Main form */}
        <form onSubmit={onSubmit} className="space-y-10">
          {/* Return & Part */}
          <Section id="return-part" title="Return & Part">
            <div className="grid sm:grid-cols-2 gap-4">
              {/* EXACT same UI behaviour: RN is read-only */}
              <TextField
                label="Returns Note Number"
                value={form.returnsNoteNumber}
                onChange={() => {}}
                readOnly
              />
              <TextField label="Serial Number" value={form.serialNumber} onChange={(v) => setF({ serialNumber: v })} error={errors.serialNumber} />
              <TextField label="SRET Number" value={form.sretNumber} onChange={(v) => setF({ sretNumber: v })} />

              {/* part loader: one read */}
              <div className="sm:col-span-2 grid grid-cols-[1fr_auto] gap-2">
                <TextField
                  label="Part Code"
                  value={form.partCode}
                  onChange={(v) => setF({ partCode: v })}
                  error={errors.partCode || errors.template}
                  placeholder="Enter exact partCode (doc id)"
                />
                <button type="button" onClick={() => loadTemplate(form.partCode)} className="self-end px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50">
                  Load
                </button>
              </div>

              <TextField label="Part Title" value={form.partTitle} onChange={(v) => setF({ partTitle: v })} placeholder="Autofilled from template; editable" />
              <TextField label="Part Description" value={form.partDescription} onChange={(v) => setF({ partDescription: v })} placeholder="Autofilled from template; editable" />
            </div>
          </Section>

          {/* Codes (same list used twice) */}
          <Section id="codes" title="Reported Reason & Fault Found (same code list)">
            {!template && <div className="text-sm text-gray-500">Load a part template to pick codes.</div>}
            {template && (
              <div className="grid sm:grid-cols-2 gap-4">
                <CodeSelect
                  label="Reported Reason"
                  value={form.reportedCode}
                  onChange={(code) => setF({ reportedCode: code })}
                  options={codesActive}
                  getOptionLabel={(c) => `${c.code} — ${c.description}`}
                  getOptionValue={(c) => c.code}
                  placeholder="Search code or description…"
                  error={errors.reportedCode}
                />
                <CodeSelect
                  label="Fault Found"
                  value={form.faultFoundCode}
                  onChange={(code) => setF({ faultFoundCode: code })}
                  options={codesActive}
                  getOptionLabel={(c) => `${c.code} — ${c.description}`}
                  getOptionValue={(c) => c.code}
                  placeholder="Search code or description…"
                  error={errors.faultFoundCode}
                />
              </div>
            )}
          </Section>

          {/* Test Procedure */}
          <Section id="procedure" title={`Test Procedure${template?.title ? ` — ${template.title}` : ""}`}>
            {!template && <div className="text-gray-500 text-sm">Load a part template to see steps.</div>}
            {template?.steps?.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4">
                {template.steps.map(step => (
                  <ProcedureField
                    key={step.id}
                    step={step}
                    value={test[step.id]}
                    onChange={(v) => setTest(prev => ({ ...prev, [step.id]: v }))}
                    error={errors[`step:${step.id}`]}
                  />
                ))}
              </div>
            )}
          </Section>

          {/* Notes */}
          <Section id="notes" title="Notes">
            <TextArea
              label="Internal Notes"
              value={form.internalNotes}
              onChange={(v) => setF({ internalNotes: v })}
              placeholder="Anything else relevant to this part…"
            />
          </Section>

          {/* Photos (same infra) */}
          <PartsPhotos draftId={draftId} />

          {/* Sticky submit */}
          <div className="h-16" />
          <div className="fixed bottom-4 left-0 right-0 px-4">
            <div className="max-w-7xl mx-auto bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex items-center gap-3">
              <span className="text-sm text-gray-600">
                Draft: {draftId ? draftId.slice(-8) : "none"} ·
                Status {STATUS_OPTIONS.find(s => s.value === status)?.label || status} ·
                Progress {Math.round(progress)}%
              </span>
              <button type="button" onClick={() => saveDraft(true)} className="ml-auto px-4 py-3 rounded-md border border-gray-300 hover:bg-gray-50" disabled={isSaving}>
                {isSaving ? "Saving…" : "Save draft"}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-md text-white transition ${isSubmitting ? "bg-indigo-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"}`}
              >
                {isSubmitting ? "Submitting…" : "Submit Part Return"}
              </button>
            </div>
          </div>

          {errors.submit && <FieldError text={errors.submit} />}
        </form>
      </div>
    </div>
  );
}

/* ---------------- Photos for Parts Returns (camera + gallery) ---------------- */
function PartsPhotos({ draftId }) {
  const [folderId, setFolderId] = useState(null);
  const [folderUrl, setFolderUrl] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({});
  const [err, setErr] = useState("");

  const fns = useMemo(() => getFunctions(undefined, "europe-west2"), []);

  // NEW: separate file inputs so iOS/Android reliably show both options
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Live storage info from draft
  useEffect(() => {
    if (!draftId) return;
    const ref = doc(db, "returns_parts_draft", draftId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const s = snap.data()?.storage;
      if (s?.folderId) {
        setFolderId(s.folderId);
        setFolderUrl(s.folderUrl || null);
      }
    });
    return () => unsub();
  }, [draftId]);

  const refreshImages = useMemo(
    () => async () => {
      if (!folderId) return;
      setLoading(true);
      setErr("");
      try {
        const list = httpsCallable(fns, "spListImages");
        const { data } = await list({ folderItemId: folderId });
        setImages(data.images || []);
      } catch (e) {
        console.error(e);
        setErr(e.message || "Failed to load images");
      } finally {
        setLoading(false);
      }
    },
    [fns, folderId]
  );

  useEffect(() => { refreshImages(); }, [refreshImages]);

  async function getUploadUrl(fileName) {
    const create = httpsCallable(fns, "spCreateUploadSession");
    const { data } = await create({ folderItemId: folderId, fileName });
    return data.uploadUrl;
  }

  async function putWithProgress(uploadUrl, file, key) {
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const pct = Math.round((e.loaded / e.total) * 100);
        setProgress((p) => ({ ...p, [key]: { name: file.name, pct } }));
      };
      xhr.onload = () => {
        setProgress((p) => ({ ...p, [key]: { name: file.name, pct: 100 } }));
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`));
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Length", String(file.size));
      xhr.setRequestHeader("Content-Range", `bytes 0-${file.size - 1}/${file.size}`);
      xhr.send(file);
    });
  }

  const onPick = async (files) => {
    if (!folderId) return;
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setBusy(true);
    try {
      for (const f of arr) {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        const url = await getUploadUrl(f.name);
        await putWithProgress(url, f, key);
      }
      await refreshImages();
    } catch (e) {
      console.error(e);
      alert(e.message || "Upload failed");
    } finally {
      setBusy(false);
      // allow re-selecting same file(s)
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const onDelete = async (itemId) => {
    if (!window.confirm("Delete this image? This cannot be undone.")) return;
    try {
      const del = httpsCallable(fns, "spDeleteDriveItem");
      await del({ itemId });
      await refreshImages();
    } catch (e) {
      console.error(e);
      alert(e.message || "Delete failed");
    }
  };

    const createFolderNow = async () => {
    if (!draftId) return;
    try {
      setBusy(true);
      const create = httpsCallable(fns, "spEnsureFolder");
      const { data } = await create({
        type: "returnsImage",
        subId: draftId,
      });
      await updateDoc(doc(db, "returns_parts_draft", draftId), {
        storage: { provider: "sharepoint", folderId: data.folderItemId, folderUrl: data.folderWebUrl },
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Could not create folder");
    } finally {
      setBusy(false);
    }
  };


  return (
    <Section id="photos" title="Photos (optional)">
      {!draftId ? (
        <p className="text-sm text-gray-500">Save a draft first to attach photos.</p>
      ) : !folderId ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-600">Folder not ready yet.</p>
          <button
            type="button"
            onClick={createFolderNow}
            disabled={busy}
            className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          >
            {busy ? "Creating…" : "Create folder"}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-sm text-gray-700 mr-1">Add photos:</span>

            {/* Take photo (camera) */}
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              onClick={() => cameraInputRef.current?.click()}
              disabled={busy}
            >
              📷 Take photo
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />

            {/* Upload from gallery */}
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              onClick={() => galleryInputRef.current?.click()}
              disabled={busy}
            >
              🖼️ Upload from gallery
            </button>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />

            {folderUrl && (
              <a
                className="ml-auto text-sm underline text-indigo-700"
                href={folderUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open folder
              </a>
            )}
            <button
              type="button"
              onClick={refreshImages}
              disabled={loading || busy}
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {Object.keys(progress).length > 0 && (
            <div className="mb-3 grid gap-2">
              {Object.entries(progress).map(([k, v]) => (
                <div key={k} className="text-xs">
                  <div className="flex justify-between">
                    <span className="truncate mr-2">{v.name}</span>
                    <span>{v.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-indigo-600 rounded" style={{ width: `${v.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {err && <p className="text-sm text-red-600 mb-2">{err}</p>}

          {loading ? (
            <p className="text-sm text-gray-500">Loading images…</p>
          ) : images.length === 0 ? (
            <p className="text-sm text-gray-500">No images uploaded.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((img) => (
                <div key={img.id} className="border rounded overflow-hidden">
                  <a href={img.webUrl} target="_blank" rel="noreferrer">
                    <img
                      src={img.thumbUrl || img.webUrl}
                      alt={img.name}
                      className="w-full h-36 object-cover"
                      loading="lazy"
                    />
                  </a>
                  <div className="p-2 text-xs flex items-center justify-between gap-2">
                    <div className="truncate" title={img.name}>{img.name}</div>
                    <button
                      type="button"
                      onClick={() => onDelete(img.id)}
                      className="text-red-600 hover:underline"
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
}


/* ----------------------- UI Bits ----------------------- */

function Section({ id, title, children }) {
  return (
    <section id={id} className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, placeholder = "", error, type = "text", readOnly = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        } ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
        value={value || ""}
        onChange={(e) => { if (!readOnly) onChange(e.target.value); }}
        placeholder={placeholder}
        readOnly={readOnly}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder = "", error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <textarea
        rows={4}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        }`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function FieldError({ text }) {
  return <p className="mt-1 text-sm text-red-600">{text}</p>;
}

/* Searchable, dependency-free select */
function CodeSelect({
  label, value, onChange, options, getOptionLabel, getOptionValue, placeholder, error,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => getOptionLabel(o).toLowerCase().includes(term));
  }, [q, options, getOptionLabel]);

  const selected = options.find((o) => getOptionValue(o) === value);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div
        className={`w-full rounded-md border px-3 py-2 flex items-center justify-between cursor-pointer ${
          error ? "border-red-500" : "border-gray-300"
        }`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-sm text-gray-800">
          {selected ? getOptionLabel(selected) : <span className="text-gray-400">Select…</span>}
        </span>
        <svg className="w-4 h-4 opacity-70" viewBox="0 0 20 20"><path d="M5 7l5 5 5-5H5z" /></svg>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2">
            <input
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              placeholder={placeholder || "Search…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-56 overflow-auto">
            {list.length === 0 && <div className="p-3 text-sm text-gray-500">No matches</div>}
            {list.map((o) => {
              const val = getOptionValue(o);
              return (
                <button
                  key={val}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${val === value ? "bg-gray-100" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(val);
                    setOpen(false);
                    setQ("");
                  }}
                >
                  {getOptionLabel(o)}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && <FieldError text={error} />}
    </div>
  );
}

/* Procedure field renderer */
function ProcedureField({ step, value, onChange, error }) {
  const base =
    "w-full rounded-md border px-3 py-2 outline-none transition " +
    (error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200");

  if (step.type === "check") {
    return (
      <label className="flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">
        <input type="checkbox" className="h-4 w-4" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <div className="flex-1">
          <div className="text-sm text-gray-800">{step.label}</div>
          {error && <FieldError text={error} />}
        </div>
      </label>
    );
  }

  if (step.type === "number") {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{step.label}</label>
        <input type="number" className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={step.placeholder || ""} />
        {error && <FieldError text={error} />}
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{step.label}</label>
      <input type="text" className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={step.placeholder || ""} />
      {error && <FieldError text={error} />}
    </div>
  );
}
